'use strict';

const { h, Component } = require('phy');
const { WithI18n } = require('@grpn/preact-l10n');
const { hydrate } = require('@grpn/hydration');
const Siema = require('siema');

const CarouselSlide = require('../../coupons/views/coupon-advert');

const AMOUNT_TO_PRELOAD = 4;

class Carousel extends Component {
  constructor(props, ...rest) {
    super(props, ...rest);
    this.siema = {};
    this.state = { currentPage: 1, rerenderedAfterInit: false };
    this.refs = { carousel: {}, selector: {} };
    this.data = {
      windowInnerWidth: 0,
      slideCount: 1,
      currentPageSize: 1,
      totalPages: 1,
      isFocused: false,
      drag: {
        active: false,
        start: 0,
        end: 0,
      },
      interval: 0,
    };

    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onClickPrev = this.onClickPrev.bind(this);
    this.onClickNext = this.onClickNext.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEndCapture = this.onClickCapture.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
  }

  componentDidMount() {
    this.setupLibrary();

    window.addEventListener('resize', this.onWindowResize);
  }

  componentWillUnmount() {
    const { siema } = this;

    if (siema) {
      siema.destroy();
    }

    window.removeEventListener('resize', this.onWindowResize);
  }

  componentDidUpdate() {
    const { siema } = this;

    if (siema) {
      siema.destroy(true, () => this.update());
    }
  }

  render() {
    const { className, coupons, navigation, routes } = this.props;
    const { totalPages, slideCount, currentPageSize } = this.data;

    return h(
      `.carousel.${className}.no-siema`,
      {
        ref: el => (this.refs.carousel = el),
        onMouseEnter: this.onMouseEnter,
        onMouseLeave: this.onMouseLeave,
        'data-bhw': 'CouponsCarousel',
      },
      [
        slideCount > currentPageSize &&
          h(
            'a.carousel-button.carousel-button-prev',
            {
              onClick: this.onClickPrev,
              'data-bhc': 'carousel-button:prev',
            },
            h('span.carousel-button-icon.icon-arrow-left-large')
          ),
        slideCount > currentPageSize &&
          h(
            'a.carousel-button.carousel-button-next',
            {
              onClick: this.onClickNext,
              'data-bhc': 'carousel-button:next',
            },
            h('span.carousel-button-icon.icon-arrow-right-large')
          ),
        h(
          '.carousel-frame',
          h(
            '.carousel-content',
            {
              ref: el => {
                this.refs.selector = el;
              },
              onMouseDown: this.onMouseDown,
              onMouseUp: this.onMouseUp,
              onClickCapture: this.onClickCapture,
              onTouchStart: this.onTouchStart,
              onTouchMove: this.onTouchMove,
              onTouchEndCapture: this.onClickCapture,
            },
            coupons.map((el, index) =>
              h(
                '.carousel-slide',
                CarouselSlide({
                  routes,
                  coupon: el,
                  lazy: index >= AMOUNT_TO_PRELOAD,
                })
              )
            )
          )
        ),
        navigation && totalPages > 1 && this.getNavigation(),
      ]
    );
  }

  setupLibrary() {
    const { selector } = this.refs;
    const { coupons, options } = this.props;
    const { currentSlide } = this.siema;

    if (coupons.length === 0) return;

    const restrictDrag = 99999999;
    const defaultOptions = {
      loop: true,
      duration: 400,
      multipleDrag: false,
      startIndex: currentSlide || 0,
      perPage: {
        1: 1,
        567: 2,
        767: 3,
        991: 4,
      },
      ...options,
    };

    this.siema = new Siema({
      selector,
      ...defaultOptions,
      threshold: restrictDrag,
    });

    this.configureData();
    this.updateClass();
    this.autoplay();
  }

  configureData() {
    const { innerElements, perPage } = this.siema;
    const { totalPages: prevTotalPages } = this.data;
    const { rerenderedAfterInit } = this.state;

    const totalPages = Math.floor(innerElements.length / perPage);

    this.data.slideCount = innerElements.length;
    this.data.currentPageSize = perPage;
    this.data.totalPages = totalPages;
    this.data.windowInnerWidth = window.innerWidth;

    if (totalPages !== prevTotalPages || !rerenderedAfterInit) {
      this.setState({
        rerenderedAfterInit: true,
      });
    }
  }

  updateClass() {
    const { carousel } = this.refs;
    const className = 'no-siema';

    carousel.classList.remove(className);
  }

  setTabIndex() {
    const {
      refs: { selector },
    } = this;

    const container = selector.getBoundingClientRect();
    const slideClass = 'a.coupon-advert';
    const slides = selector.querySelectorAll(slideClass);

    slides.forEach(el => {
      const rect = el.getBoundingClientRect();
      let tabindex = -1;

      if (rect.left >= container.left && rect.right <= container.right) {
        tabindex = 0;
      }

      el.setAttribute('tabindex', tabindex);
    });
  }

  getNavigation() {
    const { currentPage } = this.state;
    const { totalPages } = this.data;

    const pages = Array.apply(null, Array(totalPages)).map(
      (_, index) => index + 1
    );

    return h('.carousel-navigation', [
      pages.map(page =>
        h(WithI18n, {}, i18n =>
          h(
            `a.carousel-navigation-item${
              currentPage === page ? '.current' : ''
            }`,
            {
              href: 'javascript:void(0)',
              onClick: this.onClickNavigation.bind(this, page),
              'aria-label': i18n.t(
                'js.modules.shared.PaginationLinkAriaFormat',
                {
                  0: 'carousel',
                  1: page,
                }
              ),
              'data-bhc': `carousel-navigation:page-${page}`,
              title: `carousel-navigation-page-${page}`,
            }
          )
        )
      ),
    ]);
  }

  autoplay() {
    const { interval } = this.data;
    const { autoplay } = this.props;

    const autoplayInterval = 6000;
    window.clearInterval(interval);

    if (autoplay) {
      this.data.interval = window.setInterval(
        () => !this.data.isFocused && this.onClickNext(),
        autoplayInterval
      );
    }
  }

  update() {
    this.setupLibrary();
    this.setTabIndex();

    window.Groupon.Utils.lazyLoadImages();
  }

  updateCurrentPage(currentPage) {
    const {
      config: { duration },
    } = this.siema;

    window.setTimeout(() => this.setState({ currentPage }), duration);
  }

  onClickCapture(ev) {
    const {
      drag,
      drag: { start, end },
    } = this.data;
    const threshold = Math.abs(start - end);
    const dragMinimum = 40;

    if (!end || start === end) return;

    ev.preventDefault();

    if (threshold > dragMinimum) {
      ev.stopImmediatePropagation();

      if (start > end) {
        this.onClickNext();
      } else {
        this.onClickPrev();
      }
    }

    drag.start = 0;
    drag.end = 0;
  }

  onClickNavigation(page) {
    const { perPage } = this.siema;

    const goToPage = perPage * (page - 1);

    this.siema.goTo(goToPage);
    this.updateCurrentPage(page);
  }

  onClickNext() {
    const { perPage } = this.siema;
    const { currentPage } = this.state;
    const { totalPages } = this.data;

    let page = currentPage + 1;

    if (page > totalPages) {
      page = 1;
    }

    this.siema.next(perPage);
    this.updateCurrentPage(page);
  }

  onClickPrev() {
    const { perPage } = this.siema;
    const { currentPage } = this.state;
    const { totalPages } = this.data;

    let page = currentPage - 1;

    if (page < 1) {
      page = totalPages;
    }

    this.siema.prev(perPage);
    this.updateCurrentPage(page);
  }

  onMouseEnter() {
    this.data.isFocused = true;
  }

  onMouseLeave() {
    this.data.isFocused = false;
  }

  onMouseDown(ev) {
    const { drag } = this.data;

    drag.active = true;
    drag.start = ev.pageX;
  }

  onMouseUp(ev) {
    const { drag } = this.data;

    drag.active = false;
    drag.end = ev.pageX;
  }

  onTouchStart(ev) {
    const { drag } = this.data;
    const { pageX } = ev.touches[0];

    drag.active = true;
    drag.start = pageX;

    this.data.isFocused = true;
  }

  onTouchMove(ev) {
    const { drag } = this.data;
    const { pageX } = ev.changedTouches[0];

    drag.active = false;
    drag.end = pageX;
  }

  onWindowResize() {
    const { windowInnerWidth } = this.data;
    const { innerWidth } = window;

    if (windowInnerWidth === innerWidth) {
      return;
    }

    this.setState({
      currentPage: 1,
    });

    this.data.windowInnerWidth = innerWidth;
  }
}

module.exports = hydrate('Carousel', Carousel);
